// Ajustes de inventario (+/-) y carga de saldos iniciales.
// Inventario → Operaciones → Ajustes / Carga inicial.
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileSpreadsheet, Plus, SlidersHorizontal, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { useBorradorPantalla } from '@/lib/hooks';
import type { ReactNode } from 'react';
import { AjusteMotivo, BuscadorProducto, Campo, Location, Modal, ProductoOpt, UbicacionOpt, fieldCls, inv, invRpc, invalidateInventario, locationsList, money, num, productosOptList, toNum, ubicacionesList } from './shared';
import { DocumentoImprimible, DocColumn, DocMeta } from './DocumentoImprimible';
import { fechaLocal } from '@/lib/utils/fecha';
import { Trans, useTranslation } from 'react-i18next';

type PrintDoc = { titulo: string; codigo: string; meta: DocMeta[]; columns: DocColumn[]; filas: ReactNode[][]; totalLabel?: string; totalValue?: ReactNode };

// ---- Carga inicial masiva por Excel (solo modo inventario_inicial) ----------
// Normaliza encabezados/celdas para el parseo (sin acentos, mayúsculas).
const normXls = (s: unknown): string => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().trim();

// Fecha de una celda (Date de SheetJS, serial numérico o texto) → 'YYYY-MM-DD'.
function celdaFechaXls(v: unknown): string {
  if (v instanceof Date && !isNaN(v.getTime())) return v.toISOString().slice(0, 10);
  if (typeof v === 'number' && v > 0) return new Date(Date.UTC(1899, 11, 30) + v * 86400000).toISOString().slice(0, 10);
  const s = String(v ?? '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
}

interface LineaImportXls {
  producto_id: string; codigo: string; nombre: string; cantidad: number;
  costo_unitario: number; numero_lote: string; fecha_caducidad: string;
}

type Clase = 'ajuste' | 'inventario_inicial';
interface Linea {
  key: string; producto_id: string; ubicacion_id: string; numero_lote: string; fecha_caducidad: string;
  cantidad: number; costo_unitario: string; observacion: string;
}
let seq = 0;
const nuevaLinea = (): Linea => ({ key: `a${seq++}`, producto_id: '', ubicacion_id: '', numero_lote: '', fecha_caducidad: '', cantidad: 1, costo_unitario: '', observacion: '' });
const hoy = () => fechaLocal();

function AjustePantalla({ clase }: { clase: Clase }) {
  const { t } = useTranslation('inventario');
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const inicial = clase === 'inventario_inicial';
  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const { data: motivos = [] } = useQuery<AjusteMotivo[]>({
    queryKey: ['inv-ajuste-motivos-activos'],
    queryFn: () => inv.list<AjusteMotivo>('ajuste_motivos', '?select=id,nombre,tipo,es_activo&es_activo=eq.true&order=nombre'),
  });
  const { data: productos = [] } = useQuery<ProductoOpt[]>({ queryKey: ['inv-productos-opt'], queryFn: productosOptList });
  const principal = bodegas.find((b) => b.es_principal);

  const [bodegaId, setBodegaId] = useState('');
  const [fecha, setFecha] = useState(hoy());
  const [motivo, setMotivo] = useState('');
  const [obs, setObs] = useState('');
  const [lineas, setLineas] = useState<Linea[]>([nuevaLinea()]);
  const [printDoc, setPrintDoc] = useState<PrintDoc | null>(null);

  // ---- Respaldo/recuperación del ajuste en curso (cierre del navegador a medias) ----
  interface AjusteBorrador { bodegaId: string; fecha: string; motivo: string; obs: string; lineas: Linea[] }
  const { limpiar: limpiarBorrador } = useBorradorPantalla<AjusteBorrador>({
    clave: 'inv_ajuste',
    activo: true,
    snapshot: { bodegaId, fecha, motivo, obs, lineas },
    hayDatos: lineas.some((l) => l.producto_id) || !!motivo,
    onRestaurar: (d) => {
      if (Array.isArray(d.lineas) && d.lineas.length) {
        const maxSeq = d.lineas.reduce((m, l) => {
          const n = Number(String(l.key ?? '').replace(/^a/, ''));
          return Number.isFinite(n) ? Math.max(m, n) : m;
        }, -1);
        seq = Math.max(seq, maxSeq + 1);
        setLineas(d.lineas);
      }
      if (d.bodegaId) setBodegaId(d.bodegaId);
      if (d.fecha) setFecha(d.fecha);
      if (d.motivo) setMotivo(d.motivo);
      if (d.obs) setObs(d.obs);
      toast.info(t(inicial ? 'ajustes.recuperado_inicial' : 'ajustes.recuperado_ajuste', { count: d.lineas?.length ?? 0 }));
    },
  });

  // ---- Carga inicial masiva por Excel ----
  const fileRef = useRef<HTMLInputElement>(null);
  const [parseandoXls, setParseandoXls] = useState(false);
  const [previewXls, setPreviewXls] = useState<LineaImportXls[] | null>(null);
  const [noEncontradosXls, setNoEncontradosXls] = useState<string[]>([]);
  const [cargandoXls, setCargandoXls] = useState(false);
  const [progresoXls, setProgresoXls] = useState<{ hecho: number; total: number } | null>(null);
  const unidadPrincipal = principal?.unidad_venta === 'fraccion' ? 'fracciones' : 'cajas';

  const descargarPlantillaXls = async () => {
    if (productos.length === 0) { toast.error(t('ajustes.sin_productos')); return; }
    const XLSX = await import('xlsx');
    const headers = ['Código', 'Nombre', 'Presentación', 'Unidad', 'Costo ref.', 'Cantidad', 'Lote', 'Caducidad'];
    const rows = productos.map((p) => [p.codigo, p.nombre, p.presentacion || '', unidadPrincipal, Number(p.costo_referencial ?? 0), '', '', '']);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Carga inicial');
    XLSX.writeFile(wb, `carga-inicial-${hoy()}.xlsx`);
  };

  const onImportarXls = async (file: File | null) => {
    if (!file) return;
    if (!destino) { toast.error(t('ajustes.primero_bodega')); return; }
    setParseandoXls(true);
    try {
      const buf = await file.arrayBuffer();
      const XLSX = await import('xlsx');
      const wb = XLSX.read(buf, { type: 'array', cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: '' });
      if (rows.length < 2) { toast.error(t('ajustes.archivo_vacio')); return; }
      const headers = (rows[0] || []).map(normXls);
      const iCodigo = headers.indexOf('CODIGO');
      const iCantidad = headers.indexOf('CANTIDAD');
      const iLote = headers.indexOf('LOTE');
      const iCaducidad = headers.indexOf('CADUCIDAD');
      if (iCodigo < 0 || iCantidad < 0) { toast.error(t('ajustes.archivo_columnas')); return; }
      const porCodigo = new Map(productos.map((p) => [p.codigo.trim().toUpperCase(), p]));
      const encontradas: LineaImportXls[] = [];
      const faltantes: string[] = [];
      for (let r = 1; r < rows.length; r++) {
        const row = rows[r];
        if (!row || row.every((c) => c === '' || c == null)) continue;
        const cod = String(row[iCodigo] ?? '').trim();
        if (!cod) continue;
        const cantRaw = row[iCantidad];
        const cant = typeof cantRaw === 'number' ? cantRaw : Number(String(cantRaw).replace(',', '.'));
        if (!Number.isFinite(cant) || cant <= 0) continue;
        const p = porCodigo.get(cod.toUpperCase());
        if (!p) { faltantes.push(cod); continue; }
        encontradas.push({
          producto_id: p.id, codigo: p.codigo, nombre: p.nombre, cantidad: cant,
          costo_unitario: Number(p.costo_referencial ?? 0),
          numero_lote: p.maneja_lote && iLote >= 0 ? String(row[iLote] ?? '').trim() : '',
          fecha_caducidad: p.maneja_caducidad && iCaducidad >= 0 ? celdaFechaXls(row[iCaducidad]) : '',
        });
      }
      if (encontradas.length === 0) { toast.error(t('ajustes.sin_validas')); return; }
      setPreviewXls(encontradas);
      setNoEncontradosXls(faltantes);
    } catch (e) {
      toast.error(t('ajustes.err_leer_archivo', { error: getErrorMessage(e) }));
    } finally {
      setParseandoXls(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const cargarPreviewXls = async () => {
    if (!previewXls || !destino) return;
    setCargandoXls(true);
    const LOTE = 1000;
    const total = Math.ceil(previewXls.length / LOTE);
    let cargadas = 0;
    try {
      for (let i = 0; i < previewXls.length; i += LOTE) {
        const chunk = previewXls.slice(i, i + LOTE);
        setProgresoXls({ hecho: Math.floor(i / LOTE) + 1, total });
        const payload = {
          location_id: destino, clase: 'inventario_inicial', fecha: hoy(), motivo: 'Carga inicial (Excel)', observaciones: null,
          lineas: chunk.map((l) => ({
            producto_id: l.producto_id, ubicacion_id: null, cantidad: l.cantidad, costo_unitario: l.costo_unitario,
            numero_lote: l.numero_lote || null, fecha_caducidad: l.fecha_caducidad || null, observacion: null,
          })),
        };
        const r = await invRpc<{ codigo: string; total_lineas: number }>('inv_ajuste_confirmar', { payload });
        cargadas += r.total_lineas;
      }
      toast.success(t('ajustes.excel_cargado', { n: cargadas, bodega: bodegas.find((b) => b.id === destino)?.nombre ?? '' }));
      setPreviewXls(null); setNoEncontradosXls([]);
      invalidateInventario(qc);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setCargandoXls(false);
      setProgresoXls(null);
    }
  };

  const totalesPreviewXls = useMemo(() => {
    if (!previewXls) return { unidades: 0, costo: 0, costoCero: 0 };
    return previewXls.reduce((acc, l) => ({
      unidades: acc.unidades + l.cantidad,
      costo: acc.costo + l.cantidad * l.costo_unitario,
      costoCero: acc.costoCero + (l.costo_unitario === 0 ? 1 : 0),
    }), { unidades: 0, costo: 0, costoCero: 0 });
  }, [previewXls]);
  const destino = bodegaId;   // bodega elegida explícitamente; la carga inicial ya NO cae por defecto a principal
  const bodegaSel = bodegas.find((b) => b.id === destino);
  const unidadBodega = bodegaSel?.unidad_venta === 'fraccion' ? 'fracciones' : 'cajas';
  const { data: ubicaciones = [] } = useQuery<UbicacionOpt[]>({ queryKey: ['inv-ubic', destino], queryFn: () => ubicacionesList(destino), enabled: !!destino });

  const prodById = (id: string) => productos.find((p) => p.id === id);
  const setLinea = (key: string, patch: Partial<Linea>) =>
    setLineas((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const confirmar = useMutation({
    mutationFn: async () => {
      if (!destino) throw new Error(t('ajustes.err_bodega'));
      const usables = lineas.filter((l) => l.producto_id && Number(l.cantidad) !== 0);
      if (usables.length === 0) throw new Error(t('ajustes.err_lineas'));
      if (inicial && usables.some((l) => Number(l.cantidad) <= 0)) throw new Error(t('ajustes.err_cantidad_pos'));
      if (inicial && usables.some((l) => l.costo_unitario === '')) throw new Error(t('ajustes.err_costo'));
      const payload = {
        location_id: destino, clase, fecha, motivo: motivo || null, observaciones: obs || null,
        lineas: usables.map((l) => ({
          producto_id: l.producto_id, ubicacion_id: l.ubicacion_id || null, cantidad: Number(l.cantidad),
          costo_unitario: l.costo_unitario === '' ? null : Number(l.costo_unitario),
          numero_lote: l.numero_lote || null, fecha_caducidad: l.fecha_caducidad || null, observacion: l.observacion || null,
        })),
      };
      return invRpc<{ codigo: string; total_lineas: number }>('inv_ajuste_confirmar', { payload });
    },
    onSuccess: (r) => {
      toast.success(t(inicial ? 'ajustes.conf_inicial' : 'ajustes.conf_ajuste', { codigo: r.codigo, n: r.total_lineas }));
      const usables = lineas.filter((l) => l.producto_id && Number(l.cantidad) !== 0);
      const prodLabel = (id: string) => { const p = productos.find((x) => x.id === id); return p ? `${p.codigo} · ${p.nombre}` : id; };
      setPrintDoc({
        titulo: inicial ? t('ajustes.titulo_inicial') : t('ajustes.doc_ajuste'), codigo: r.codigo,
        meta: [
          { label: t('ajustes.fecha'), value: fecha },
          { label: t('ajustes.bodega'), value: bodegas.find((b) => b.id === destino)?.nombre ?? '—' },
          { label: t('ajustes.motivo'), value: motivo || '—' },
        ],
        columns: [{ header: t('ajustes.producto') }, { header: t('ajustes.cantidad'), right: true }, { header: t('ajustes.costo_unit'), right: true }, { header: t('ajustes.lote') }],
        filas: usables.map((l) => [prodLabel(l.producto_id), num(l.cantidad, 2), l.costo_unitario === '' ? '—' : money(Number(l.costo_unitario)), l.numero_lote || '—']),
      });
      setLineas([nuevaLinea()]); setMotivo(''); setObs('');
      limpiarBorrador();
      invalidateInventario(qc);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const totalCosto = useMemo(
    () => lineas.reduce((s, l) => s + Math.abs(Number(l.cantidad) || 0) * (Number(l.costo_unitario) || 0), 0),
    [lineas],
  );

  // Validación de cabecera/líneas (la RPC vuelve a validar en el servidor)
  const faltantes = useMemo(() => {
    const f: string[] = [];
    if (!destino) f.push(t('ajustes.f_bodega'));
    if (!inicial && !motivo.trim()) f.push(t('ajustes.f_motivo'));
    if (!fecha) f.push(t('ajustes.f_fecha'));
    else if (fecha > hoy()) f.push(t('ajustes.f_fecha_futura'));
    if (!lineas.some((l) => l.producto_id && Number(l.cantidad) !== 0)) f.push(t('ajustes.f_linea'));
    return f;
  }, [destino, inicial, motivo, fecha, lineas, t]);

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <h1 className="text-xl font-semibold flex items-center gap-2">
        {inicial ? <Upload className="w-5 h-5" /> : <SlidersHorizontal className="w-5 h-5" />}
        {inicial ? t('ajustes.titulo_inicial') : t('ajustes.titulo')}
      </h1>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t('ajustes.cabecera')}</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Campo label={t('ajustes.bodega_req')}>
            <select className={fieldCls} value={destino} onChange={(e) => setBodegaId(e.target.value)}>
              <option value="">{t('ajustes.selecciona')}</option>
              {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}{b.es_principal ? ` ${t('ajustes.principal')}` : ''}</option>)}
            </select>
            {destino && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t('ajustes.cantidades_en')} <b>{unidadBodega}</b> {t('ajustes.unidad_venta_bodega')}
              </p>
            )}
          </Campo>
          {inicial ? (
            <Campo label={t('ajustes.fecha_req')}><input type="date" className={fieldCls} value={fecha} max={hoy()} onChange={(e) => setFecha(e.target.value)} /></Campo>
          ) : (
            <Campo label={t('ajustes.fecha')}>
              <input className={`${fieldCls} bg-muted/50 cursor-not-allowed`} value={fecha} readOnly title={t('ajustes.fecha_hoy')} />
            </Campo>
          )}
          <Campo label={inicial ? t('ajustes.motivo') : t('ajustes.motivo_req')} className="md:col-span-2">
            <select className={fieldCls} value={motivo} onChange={(e) => setMotivo(e.target.value)}>
              <option value="">{inicial ? t('ajustes.motivo_inicial') : t('ajustes.seleccione_motivo')}</option>
              {motivos.map((m) => <option key={m.id} value={m.nombre}>{m.nombre}</option>)}
            </select>
          </Campo>
          <Campo label={t('ajustes.observaciones')} className="md:col-span-4"><input className={fieldCls} value={obs} onChange={(e) => setObs(e.target.value)} /></Campo>
        </CardContent>
      </Card>

      {inicial && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" /> {t('ajustes.carga_masiva')}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              <Trans i18nKey="ajustes.excel_ayuda" ns="inventario" components={{ b: <b /> }} />
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" onClick={() => void descargarPlantillaXls()}>
                <Download className="w-4 h-4 mr-1" /> {t('ajustes.descargar_plantilla')}
              </Button>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => void onImportarXls(e.target.files?.[0] ?? null)} />
              <Button variant="outline" isLoading={parseandoXls} disabled={!destino} onClick={() => fileRef.current?.click()}>
                <Upload className="w-4 h-4 mr-1" /> {t('ajustes.importar_excel')}
              </Button>
              {destino
                ? <span className="text-xs text-muted-foreground">{t('ajustes.destino')}: <b>{bodegaSel?.nombre}</b></span>
                : <span className="text-xs text-amber-600 dark:text-amber-400">{t('ajustes.selecciona_destino')}</span>}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">{t('ajustes.lineas', { n: lineas.length })}</CardTitle>
          <Button variant="outline" onClick={() => setLineas((ls) => [...ls, nuevaLinea()])}><Plus className="w-4 h-4 mr-1" /> {t('ajustes.linea')}</Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-2">{t('ajustes.producto')}</th>
              <th className="py-2 pr-2 w-40">{t('ajustes.ubicacion')}</th>
              <th className="py-2 pr-2 w-28 text-right">{inicial ? t('ajustes.cantidad') : t('ajustes.cantidad_pm')}</th>
              <th className="py-2 pr-2 w-28 text-right">{t('ajustes.costo_unit')}{inicial ? ' *' : ''}</th>
              <th className="py-2 pr-2 w-32">{t('ajustes.lote')}</th><th className="py-2 pr-2 w-36">{t('ajustes.caducidad')}</th>
              <th className="py-2 pr-2">{t('ajustes.observacion')}</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {lineas.map((l) => {
                const p = prodById(l.producto_id);
                const lote = p?.maneja_lote || p?.maneja_caducidad;
                return (
                  <tr key={l.key} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-2 min-w-56">
                      <BuscadorProducto productos={productos} value={l.producto_id} compact
                        onChange={(id) => setLinea(l.key, { producto_id: id })} />
                    </td>
                    <td className="py-2 pr-2">
                      <select className={`${fieldCls} h-9`} value={l.ubicacion_id} onChange={(e) => setLinea(l.key, { ubicacion_id: e.target.value })}>
                        <option value="">{t('ajustes.general_defecto')}</option>
                        {ubicaciones.map((u) => <option key={u.id} value={u.id}>{u.codigo}{u.nombre ? ' — ' + u.nombre : ''}</option>)}
                      </select>
                    </td>
                    <td className="py-2 pr-2"><input type="number" step="0.0001" className={`${fieldCls} h-9 text-right`} value={l.cantidad} onChange={(e) => setLinea(l.key, { cantidad: toNum(e.target.value) })} /></td>
                    <td className="py-2 pr-2"><input type="number" step="0.0001" className={`${fieldCls} h-9 text-right`} placeholder={inicial ? '0.00' : t('ajustes.promedio')} value={l.costo_unitario} onChange={(e) => setLinea(l.key, { costo_unitario: e.target.value })} /></td>
                    <td className="py-2 pr-2"><input className={`${fieldCls} h-9`} placeholder={lote ? t('ajustes.lote_ph') : '—'} value={l.numero_lote} onChange={(e) => setLinea(l.key, { numero_lote: e.target.value })} /></td>
                    <td className="py-2 pr-2"><input type="date" className={`${fieldCls} h-9`} value={l.fecha_caducidad} onChange={(e) => setLinea(l.key, { fecha_caducidad: e.target.value })} /></td>
                    <td className="py-2 pr-2"><input className={`${fieldCls} h-9`} value={l.observacion} onChange={(e) => setLinea(l.key, { observacion: e.target.value })} /></td>
                    <td className="py-2 text-right"><button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => setLineas((ls) => ls.filter((x) => x.key !== l.key))}><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!inicial && <p className="mt-3 text-xs text-muted-foreground">{t('ajustes.nota_cantidad')}</p>}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between gap-4">
        <div className="text-sm text-muted-foreground">{t('ajustes.total_estimado')}: <span className="font-semibold text-foreground">{money(totalCosto)}</span></div>
        <div className="flex items-center gap-3">
          {faltantes.length > 0 && (
            <span className="text-xs text-amber-600 dark:text-amber-400">{t('ajustes.falta', { lista: faltantes.join(', ') })}</span>
          )}
          <Button isLoading={confirmar.isPending} disabled={faltantes.length > 0} onClick={async () => {
            const n = lineas.filter((l) => l.producto_id && Number(l.cantidad) !== 0).length;
            const b = bodegas.find((x) => x.id === destino)?.nombre ?? '';
            if (await dlg.confirmar(t(inicial ? 'ajustes.confirmar_inicial' : 'ajustes.confirmar_ajuste', { n, b }), { detalle: t('ajustes.detalle_stock') })) confirmar.mutate();
          }}>
            {inicial ? t('ajustes.btn_inicial') : t('ajustes.btn_ajuste')}
          </Button>
        </div>
      </div>

      {printDoc && <DocumentoImprimible {...printDoc} onClose={() => setPrintDoc(null)} />}

      {previewXls && (
        <Modal title={t('ajustes.preview_titulo')} onClose={() => { if (!cargandoXls) { setPreviewXls(null); setNoEncontradosXls([]); } }} wide>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="rounded border p-2"><p className="text-xs text-muted-foreground">{t('ajustes.lineas_cargar')}</p><p className="font-semibold">{previewXls.length}</p></div>
              <div className="rounded border p-2"><p className="text-xs text-muted-foreground">{t('ajustes.no_encontrados')}</p><p className="font-semibold">{noEncontradosXls.length}</p></div>
              <div className="rounded border p-2"><p className="text-xs text-muted-foreground">{t('ajustes.total_unidades')}</p><p className="font-semibold">{num(totalesPreviewXls.unidades)}</p></div>
              <div className="rounded border p-2"><p className="text-xs text-muted-foreground">{t('ajustes.total_costo')}</p><p className="font-semibold">{money(totalesPreviewXls.costo)}</p></div>
            </div>

            {noEncontradosXls.length > 0 && (
              <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-2 text-xs">
                <p className="font-medium text-amber-700 dark:text-amber-300">{t('ajustes.codigos_no_encontrados')}</p>
                <p className="font-mono text-amber-700 dark:text-amber-300">
                  {noEncontradosXls.slice(0, 10).join(', ')}{noEncontradosXls.length > 10 ? ` ${t('ajustes.mas', { n: noEncontradosXls.length - 10 })}` : ''}
                </p>
              </div>
            )}

            {totalesPreviewXls.costoCero > 0 && (
              <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-2 text-xs text-amber-700 dark:text-amber-300">
                {t('ajustes.costo_cero', { n: totalesPreviewXls.costoCero })}
              </div>
            )}

            <div className="max-h-60 overflow-y-auto rounded border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50"><tr>
                  <th className="py-1 px-2 text-left">{t('ajustes.codigo')}</th><th className="py-1 px-2 text-left">{t('ajustes.nombre')}</th>
                  <th className="py-1 px-2 text-right">{t('ajustes.cantidad')}</th><th className="py-1 px-2 text-right">{t('ajustes.costo_unit')}</th>
                </tr></thead>
                <tbody>
                  {previewXls.slice(0, 200).map((l) => (
                    <tr key={l.producto_id} className="border-t">
                      <td className="py-1 px-2 font-mono">{l.codigo}</td><td className="py-1 px-2">{l.nombre}</td>
                      <td className="py-1 px-2 text-right">{num(l.cantidad)}</td><td className="py-1 px-2 text-right">{money(l.costo_unitario)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {previewXls.length > 200 && <p className="p-2 text-xs text-muted-foreground">{t('ajustes.mostrando', { n: previewXls.length })}</p>}
            </div>

            {progresoXls && <p className="text-xs text-muted-foreground">{t('ajustes.cargando_lote', { hecho: progresoXls.hecho, total: progresoXls.total })}</p>}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" disabled={cargandoXls} onClick={() => { setPreviewXls(null); setNoEncontradosXls([]); }}>{t('comun.cancelar')}</Button>
              <Button isLoading={cargandoXls} disabled={!destino} onClick={() => void cargarPreviewXls()}>{t('ajustes.cargar_a', { bodega: bodegaSel?.nombre ?? '—' })}</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function AjusteInventario() { return <AjustePantalla clase="ajuste" />; }
export function CargaInicial() { return <AjustePantalla clase="inventario_inicial" />; }
